import type { InternalClerkScriptProps } from '@clerk/shared/types';
import { isTruthy } from '@clerk/shared/underscore';

import type { AstroClerkIntegrationParams, InternalRuntimeOptions } from '../types';

/**
 * Merges `prefetchUI` param with env vars.
 * - If param `prefetchUI` is explicitly `false`, return `false`
 * - If env `PUBLIC_CLERK_PREFETCH_UI` is "false", return `false`
 * - Otherwise return `undefined` (default behavior: prefetch UI)
 */
function mergePrefetchUIConfig(paramPrefetchUI: AstroClerkIntegrationParams['prefetchUI']): boolean | undefined {
  // Explicit false from param takes precedence
  if (paramPrefetchUI === false) {
    return false;
  }

  // Check env var
  if (import.meta.env.PUBLIC_CLERK_PREFETCH_UI === 'false') {
    return false;
  }

  return undefined;
}

/**
 * @internal
 */
const mergeEnvVarsWithParams = (
  params?: AstroClerkIntegrationParams & InternalRuntimeOptions & InternalClerkScriptProps,
) => {
  const {
    signInUrl: paramSignIn,
    signUpUrl: paramSignUp,
    isSatellite: paramSatellite,
    proxyUrl: paramProxy,
    domain: paramDomain,
    publishableKey: paramPublishableKey,
    telemetry: paramTelemetry,
    __internal_clerkJSUrl: paramClerkJSUrl,
    __internal_clerkJSVersion: paramClerkJSVersion,
    __internal_clerkUIUrl: paramClerkUIUrl,
    __internal_clerkUIVersion: paramClerkUIVersion,
    prefetchUI: paramPrefetchUI,
    unsafe_disableDevelopmentModeConsoleWarning: paramUnsafeDisableDevelopmentModeConsoleWarning,
    ...rest
  } = params || {};

  const internalOptions = params;

  return {
    signInUrl: paramSignIn || import.meta.env.PUBLIC_CLERK_SIGN_IN_URL,
    signUpUrl: paramSignUp || import.meta.env.PUBLIC_CLERK_SIGN_UP_URL,
    isSatellite: paramSatellite || import.meta.env.PUBLIC_CLERK_IS_SATELLITE,
    proxyUrl: paramProxy || import.meta.env.PUBLIC_CLERK_PROXY_URL,
    domain: paramDomain || import.meta.env.PUBLIC_CLERK_DOMAIN,
    // In keyless mode, use server-injected publishableKey from params
    publishableKey:
      paramPublishableKey || internalOptions?.publishableKey || import.meta.env.PUBLIC_CLERK_PUBLISHABLE_KEY || '',
    __internal_clerkJSUrl: paramClerkJSUrl || import.meta.env.PUBLIC_CLERK_JS_URL,
    __internal_clerkJSVersion: paramClerkJSVersion || import.meta.env.PUBLIC_CLERK_JS_VERSION,
    __internal_clerkUIUrl: paramClerkUIUrl || import.meta.env.PUBLIC_CLERK_UI_URL,
    __internal_clerkUIVersion: paramClerkUIVersion || import.meta.env.PUBLIC_CLERK_UI_VERSION,
    prefetchUI: mergePrefetchUIConfig(paramPrefetchUI),
    telemetry: paramTelemetry || {
      disabled: isTruthy(import.meta.env.PUBLIC_CLERK_TELEMETRY_DISABLED),
      debug: isTruthy(import.meta.env.PUBLIC_CLERK_TELEMETRY_DEBUG),
    },
    unsafe_disableDevelopmentModeConsoleWarning:
      paramUnsafeDisableDevelopmentModeConsoleWarning ??
      isTruthy(import.meta.env.PUBLIC_CLERK_UNSAFE_DISABLE_DEVELOPMENT_MODE_CONSOLE_WARNING),
    // Read from params (server-injected via __CLERK_ASTRO_SAFE_VARS__)
    // These are dynamically resolved by middleware, not from env vars
    __internal_keylessClaimUrl: internalOptions?.keylessClaimUrl,
    __internal_keylessApiKeysUrl: internalOptions?.keylessApiKeysUrl,
    ...rest,
  };
};

export { mergeEnvVarsWithParams };
